// @core/stores/user/UserReferralStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { userApi } from '@core/api';
import type {
  GenerateReferralLinkRequest,
  GenerateReferralLinkResponse,
  InviteUserRequest,
  User,
} from '@core/types/user';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class UserReferralStore {
  // List state
  items: User[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: User | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, User>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): User | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: User | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Generate personal referral link
   */
  async GenerateReferralLink(user_id: string, data: GenerateReferralLinkRequest): Promise<GenerateReferralLinkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userApi.GenerateReferralLink(user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Invite a new user
   */
  async InviteUser(data: InviteUserRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userApi.InviteUser(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List users invited by current user
   */
  async ListInvitedUsers(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userApi.ListInvitedUsers(params);

      runInAction(() => {
        this.items = response.users?.users ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const userReferralStore = new UserReferralStore();
